import { useNavigate } from '@tanstack/react-router'
import { Checkbox } from '@trellis/roots/checkbox'
import { DatePicker } from '@trellis/roots/forms'
import { Label } from '@trellis/roots/label'
import { FormDialog } from '@trellis/roots/overlays'
import { Typography } from '@trellis/roots/typography'
import { calendarDateToUtcMidnight } from '@trellis/shared'
import { useId, useState } from 'react'
import { toast } from 'sonner'

import { InvoiceNumberField } from '@/components/invoice-number-field'
import {
  useInvoiceQuery,
  useNextInvoiceNumberQuery,
  useReissueInvoiceMutation,
} from '@/hooks/useInvoices'
import { useDefaultDueDate, useOrganizationToday } from '@/hooks/useOrganization'

interface ReissueInvoiceDialogProps {
  invoiceId: string | null
  onOpenChange: (open: boolean) => void
}

/**
 * Re-issues an OVERDUE or CANCELLED invoice as a new DRAFT. Loads the invoice detail
 * itself because the expense count and the "already re-issued" state are not on list
 * rows.
 */
export function ReissueInvoiceDialog({
  invoiceId,
  onOpenChange,
}: ReissueInvoiceDialogProps) {
  const navigate = useNavigate()
  const relinkCheckboxId = useId()
  const open = !!invoiceId
  const { data: invoiceResult, isLoading } = useInvoiceQuery(invoiceId ?? '')
  const { data: nextInvoiceNumberResult } = useNextInvoiceNumberQuery(open)
  const reissueMutation = useReissueInvoiceMutation()

  const organizationToday = useOrganizationToday()
  const [issueDateInput, setIssueDateInput] = useState<string | null>(null)
  const [dueDateInput, setDueDateInput] = useState<string | null>(null)
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [blocked, setBlocked] = useState(false)
  const [relinkExpenses, setRelinkExpenses] = useState(true)

  const issueDate = issueDateInput ?? organizationToday
  const defaultDueDate = useDefaultDueDate(issueDate)
  const dueDate = dueDateInput ?? defaultDueDate

  function handleReset() {
    setIssueDateInput(null)
    setDueDateInput(null)
    setInvoiceNumber('')
    setBlocked(false)
    setRelinkExpenses(true)
  }

  const invoice = invoiceResult?.data ?? null
  const isDueBeforeIssue = dueDate < issueDate
  const blockedReason = invoice?.reissuedAs
    ? `${invoice.invoiceNumber} has already been re-issued as ${invoice.reissuedAs.invoiceNumber}.`
    : invoice?.hasSucceededPayment
      ? `${invoice.invoiceNumber} has a payment recorded, so it cannot be re-issued. Duplicate it instead.`
      : null
  const linkedExpenseCount = invoice?.linkedExpenseCount ?? 0

  function handleConfirm() {
    if (!invoice || blocked || blockedReason || isDueBeforeIssue) return
    reissueMutation.mutate(
      {
        id: invoice.id,
        data: {
          issueDate: calendarDateToUtcMidnight(issueDate).toISOString(),
          dueAt: calendarDateToUtcMidnight(dueDate).toISOString(),
          invoiceNumber: invoiceNumber || undefined,
          ...(linkedExpenseCount > 0 ? { relinkExpenses } : {}),
        },
      },
      {
        onSuccess: (result) => {
          onOpenChange(false)
          toast.success('Invoice re-issued', {
            description: `${result.data.invoiceNumber} is ready to send.`,
          })
          void navigate({ to: '/invoices/$id', params: { id: result.data.id } })
        },
        onError: (caughtError) => {
          const message =
            caughtError instanceof Error
              ? caughtError.message
              : 'Failed to re-issue invoice'
          toast.error('Failed to re-issue invoice', { description: message })
        },
      },
    )
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Re-issue invoice"
      description={`Creates a new DRAFT copy of ${invoice?.invoiceNumber ?? 'this invoice'} with fresh dates.${
        invoice?.status === 'OVERDUE'
          ? ` ${invoice.invoiceNumber} will be cancelled.`
          : ''
      }`}
      submitLabel="Re-issue"
      pendingLabel="Re-issuing..."
      isPending={reissueMutation.isPending}
      submitDisabled={
        isLoading || !invoice || blocked || !!blockedReason || isDueBeforeIssue
      }
      onSubmit={handleConfirm}
      onReset={handleReset}
    >
      {blockedReason ? (
        <Typography variant="body-sm" color="destructive-text">
          {blockedReason}
        </Typography>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="reissueIssueDate">Issue date</Label>
          <DatePicker
            id="reissueIssueDate"
            value={issueDate}
            onChange={setIssueDateInput}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reissueDueDate">Due date</Label>
          <DatePicker
            id="reissueDueDate"
            value={dueDate}
            onChange={setDueDateInput}
            required
          />
        </div>
      </div>
      {isDueBeforeIssue ? (
        <Typography variant="body-sm" color="destructive-text">
          Due date cannot be before the issue date.
        </Typography>
      ) : null}

      <InvoiceNumberField
        id="reissueInvoiceNumber"
        value={invoiceNumber}
        onChange={setInvoiceNumber}
        prefillValue={nextInvoiceNumberResult?.data?.invoiceNumber}
        disclaimer="New invoice references will always continue from the highest reference in use — including any custom reference you set here."
        onAvailabilityChange={setBlocked}
      />

      {linkedExpenseCount > 0 && invoice ? (
        <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <Typography variant="body-sm">
            {linkedExpenseCount === 1
              ? '1 reimbursement expense is'
              : `${linkedExpenseCount} reimbursement expenses are`}{' '}
            billed on {invoice.invoiceNumber}. They will stay linked to the cancelled
            invoice unless you move them.
          </Typography>
          <div className="flex items-start gap-2.5">
            <Checkbox
              id={relinkCheckboxId}
              checked={relinkExpenses}
              onCheckedChange={(isChecked) => setRelinkExpenses(isChecked === true)}
            />
            <Label htmlFor={relinkCheckboxId} className="font-normal">
              Move linked expenses to the new invoice
            </Label>
          </div>
          {!relinkExpenses ? (
            <Typography variant="body-sm" color="muted">
              You will need to assign these expenses to the new invoice yourself later.
            </Typography>
          ) : null}
        </div>
      ) : null}
    </FormDialog>
  )
}
